
export interface DispatchBoardQueueLoad {
    picklistNumber?: number;
    manifestNumber: number;
    status: DispatchBoardQueueStatus;
    routeName: string;
    door: string;
    trailerNumber: string;
    tractorNumber: string;
    driverNumber: number;
    carrierNumber: number;
    stops: number;
    probills: number;
    pieces: number;
    weight: number;
    loadedProbills: number;
    loadedPieces: number;
    loadedWeight: number;
    deliveredProbills: number;
    deliveredPieces: number;
    deliveredWeight: number;
    plannedDispatchedAt: Date;
    dispatchedAt: Date;
    earliestEta: Date;
    latestEta: Date;
    driverName: string;
    carrierName: string;
}

export enum DispatchBoardQueueStatus {
    READY_TO_ASSIGN = "READY_TO_ASSIGN",
    LOADING = "LOADING",
    READY_TO_CLOSE = "READY_TO_CLOSE",
    READY_TO_DISPATCH = "READY_TO_DISPATCH",
    DISPATCHED = "DISPATCHED"
}

export interface GetDispatchBoardQueueRequest {
    terminalNumber: number | undefined;
}

export interface GetDispatchBoardQueueResponse {
    loads: DispatchBoardQueueLoad[];
}

export interface GetDispatchBoardQueueLoadRequest {
    terminalNumber: number | undefined;
    picklistNumber: number | undefined;
    manifestNumber: number | undefined;
}

export interface GetDispatchBoardQueueLoadResponse {
    load: DispatchBoardQueueLoad;
}

export interface GetDispatchBoardQueueLoadDetailsRequest {
    terminalNumber: number | undefined;
    picklistNumber: number | undefined;
    manifestNumber: number | undefined;
}

export interface GetDispatchBoardQueueLoadDetailsResponse {
    loadDetails: DispatchBoardQueueLoadDetail[];
}

export interface DispatchBoardQueueLoadDetail {
      sequenceNumber: number;
      sequenceType: DispatchBoardQueueLoadDetailSequenceType;
      probills: DispatchBoardQueueLoadDetailProbill[];
}

export enum DispatchBoardQueueLoadDetailSequenceType {
    DELIVERY = "DELIVERY",
    LOADING = "LOADING"
}

export enum ProbillStatus {
    DELIVERED = "DELIVERED",
    LOADED = "LOADED",
    IN_PROGRESS = "IN_PROGRESS",
    PENDING = "PENDING",
    EXCEPTIONS = "EXCEPTIONS"
}

export interface DispatchBoardQueueLoadDetailProbill {
    probillNumber: number;
    sequenceNumber: number;
    bolNumber: string;
    poNumber: string;
    shipperNumber: number;
    shipperName: string;
    consigneeNumber: number;
    consigneeName: string;
    consigneeAddress: string;
    /** @deprecated Use routeName and routeNumber */
    route?: number;
    routeNumber?: number;
    routeName?: string;
    probillStatus?: ProbillStatus;
    items: DispatchBoardQueueLoadDetailItem[];
}

export interface DispatchBoardQueueLoadDetailItem {
    itemDesc: string;
    itemNumber: string;
    itemNumberType: string;
    itemSequenceNumber: number;
    location: string;
    type: string;
    pieces: number;
    weight: number;
    size: string;
    squareYards: number;
    status: DispatchBoardQueueLoadDetailItemStatus;
    length: string;
    width: string;
    height: string;
    sku: string;
    rollNumber: string;
    style: string;
    color: string;
    itemPoNumber: string;
    serialNumber: string;
    commodityCode: string;
    dyelot: string;
    sidemark: string;
    partNumber: string;
    updatedAt: string; // ISO 8601 format
    updatedBy: string;
}

export enum DispatchBoardQueueLoadDetailItemStatus {
    'City Delivery Loaded',
    'Damaged',
    'Headload',
    'Loaded',
    'Overage',
    'On Pole',
    'Rack',
    'Staged',
    'Shortage',
    'Unloaded',
    'Dispatched',
    'Lost'
}

export interface Door {
    terminalNumber: number;
    doorNumber: string;
    doorType: DoorType;
    primaryDistributorNumber: number;
    primaryDistributorName: string;
    doorPriority: number;
    trailerNumber: number;
    manifestNumber: number;
    carrierNumber: number;
    carrierName: string;
}

export enum DoorType {
    LOADING = "LOADING",
    UNLOADING = "UNLOADING",
    CITY_DELIVERY = "CITY_DELIVERY",
    STAGING = "STAGING",
    RACK = "RACK",
    OVERAGES = "OVERAGES",
    SHORTEGES = "SHORTEGES",
}

/** Returns a human-friendly display label for a DoorType (e.g. CITY_DELIVERY -> "City Delivery"). */
export function getDoorTypeDisplayLabel(doorType: DoorType | string): string {
    return String(doorType)
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

export interface GetDoorsRequest {
    terminalNumber: number | undefined;
    doorNumberSearch?: string | undefined;
    doorTypes?: DoorType[] | undefined;
}

export interface GetDoorsResponse {
    doors: Door[];
}

export interface SetupDoorRequest {
    terminalNumber: number | undefined;
    doorNumber: string | undefined;
    picklistNumber: number | undefined;
    trailerNumber: number | undefined;
    carrierNumber: number | undefined;
}

export interface SetupDoorResponse {
    success?: boolean; // true if the operation is successful
    detail?: any; // this property provides error details, parse as string for UI display
}

export interface CloseDoorRequest {
    terminalNumber: number | undefined;
    doorNumber: string | undefined;
    picklistNumber: number | undefined;
    trailerNumber: string | undefined;
    carrierNumber: number | undefined;
}

export interface CloseDoorResponse {
    success?: boolean; // true if the operation is successful
    detail?: any; // this property provides error details, parse as string for UI display
    delivery_manifest_number?: string; // the manifest number of the delivery manifest that was created
}

export interface DispatchManifestRequest {
    terminalNumber: number | undefined;
    manifestNumber: number | undefined;
    trailerNumber: string | undefined;
    tractorNumber: string | undefined;
    driverNumber: number | undefined;
    carrierNumber: number | undefined;
    size: TrailerSize | undefined;
    dispatchAt: Date | undefined; // ISO 8601 timestamp (e.g. 2026-02-19T18:30:00Z)
}

export enum TrailerSize {
    TWO_EIGHT = "28",
    THREE_TWO = "32",
    FOUR_ZERO = "40",
    FOUR_FIFTY = "45",
    FOUR_EIGHT = "48",
    FIFTY_THREE = "53"
}

/** Human-friendly label for TrailerSize (e.g. "28" -> "28 Feet"). */
export function getTrailerSizeDisplayLabel(size: TrailerSize | string): string {
    return `${size} Feet`;
}

export interface DispatchManifestResponse {
    success?: boolean; // true if the operation is successful
    detail?: any; // this property provides error details, parse as string for UI display
}